import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { resetPassword } from "./auth";
import "./Login.css";

function ResetForm({ email, setIsResetPass }) {
  const { t } = useTranslation("login");
  const [formData, setFormData] = useState({
    password: "",
    passwordConfirm: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetPassError, setResetPassError] = useState("");            // ← new

  const validate = (data) => {
    const errs = {};
    const { password, passwordConfirm } = data;

    if (!password) {
      errs.password = t("ResetForm.passwordRequired");
    } else if (password.length < 8) {
      errs.password = t("ResetForm.passwordMinLength");
    } else if (!/[A-Z]/.test(password)) {
      errs.password = t("ResetForm.passwordUppercase");
    } else if (!/[a-z]/.test(password)) {
      errs.password = t("ResetForm.passwordLowercase");
    } else if (!/[0-9]/.test(password)) {
      errs.password = t("ResetForm.passwordNumber");
    } else if (!/[^A-Za-z0-9]/.test(password)) {
      errs.password = t("ResetForm.passwordSpecialChar");
    }

    if (!passwordConfirm) {
      errs.passwordConfirm = t("ResetForm.confirmPasswordRequired");
    } else if (passwordConfirm !== password) {
      errs.passwordConfirm = t("ResetForm.passwordsMustMatch");
    }

    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newData = { ...formData, [name]: value };
    setFormData(newData);
    setErrors(validate(newData));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword((prev) => !prev);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate(formData);
    setErrors(validationErrors);
    setTouched({ password: true, passwordConfirm: true });
    if (Object.keys(validationErrors).length > 0) return;

    setLoading(true);
    setResetPassError("");                                            // reset general error

    try {
      const values = { ...formData, email };
      const response = await resetPassword(values);
      if (response?.status === 200 || response?.status === 201) {
        setIsPasswordReset(true);
        return;
      }
    } catch (error) {
      console.log("Full error object:", error);
      const resp = error?.response;
      const data = resp?.data || {};

      // 1) Top-level error message
      if (data.error) {
        setResetPassError(data.error);
      }

      // 2) Field-level errors[]
      const backendErrors = data.errors;
      if (Array.isArray(backendErrors)) {
        const mapped = backendErrors.reduce(
          (acc, errObj) => ({ ...acc, ...errObj }),
          {}
        );
        setErrors((prev) => ({ ...prev, ...mapped }));
      }

      // 3) If no errors[] and no data.error, fallback:
      if (!data.error && !Array.isArray(backendErrors)) {
        const msg =
          data.message ||
          error?.message ||
          t("ResetForm.unexpectedError", "An unexpected error occurred.");
        setResetPassError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (isPasswordReset) {
    return (
      <div className="success-message">
        <img src="/tick-circle.svg" alt="Success" />
        <h1 className="title-login">{t("ResetForm.successTitle")}</h1>
        <p className="subtitle-login">{t("ResetForm.successSubtitle")}</p>
        <button className="login-btn" onClick={() => setIsResetPass(false)}>
          {t("ResetForm.continueToLogin")}
        </button>
      </div>
    );
  }

  return (
    <>
      <h2 className="title-login">{t("ResetForm.title")}</h2>

      {/* display general backend error */}
      {resetPassError && <p className="login-error">{resetPassError}</p>}

      <form onSubmit={handleSubmit}>
        <div className="form-group-unique">
          <label htmlFor="password">{t("ResetForm.newPasswordLabel")}</label>
          <div className="password-input-unique">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <span
              className="toggle-password-unique"
              onClick={togglePasswordVisibility}
            >
              <img
                src={showPassword ? "/eye.svg" : "/eye-slash.svg"}
                alt="toggle visibility"
              />
            </span>
          </div>
          {touched.password && errors.password && (
            <p className="login-error">{errors.password}</p>
          )}
        </div>

        <div className="form-group-unique">
          <label htmlFor="passwordConfirm">
            {t("ResetForm.confirmPasswordLabel")}
          </label>
          <div className="password-input-unique">
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="passwordConfirm"
              name="passwordConfirm"
              value={formData.passwordConfirm}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <span
              className="toggle-password-unique"
              onClick={toggleConfirmPasswordVisibility}
            >
              <img
                src={showConfirmPassword ? "/eye.svg" : "/eye-slash.svg"}
                alt="toggle visibility"
              />
            </span>
          </div>
          {touched.passwordConfirm && errors.passwordConfirm && (
            <p className="login-error">{errors.passwordConfirm}</p>
          )}
        </div>

        <div className="form-group-login">
          <button
            disabled={
              loading ||
              Object.keys(errors).length > 0 ||
              !formData.password ||
              !formData.passwordConfirm
            }
            className="login-btn"
            type="submit"
          >
            {loading ? t("ResetForm.submitting") : t("ResetPassword.submit")}
          </button>
        </div>
      </form>
    </>
  );
}

export default ResetForm;
